// SCR-STAFF-907 — پروفایل عمومی مرکز (US-PLT-073): پیش‌نویس فیلدها (معرفی، نشانی، تلفن، وب‌سایت، ساعات کاری، اینستاگرام، نقشه)
// با علامت «عمومی» هر فیلد؛ انتشار فقط فیلدهای عمومی غیرخالی را در نسخه منتشرشده می‌گذارد (AC-PLT-073.1) و رویداد
// platform.tenant.public_profile_updated.v1 برای CHN منتشر می‌شود. تصاویر به DOC-01 موکول است (C-AGENT-417).
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type PublicProfileField, type PublicProfileView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormPage, JalaliDate, KeyValueList } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { pltKeys, toCount, WriteNotice } from './common';

export const SCR_STAFF_907 = 'SCR-STAFF-907';

/** فیلدهای پروفایل عمومی (PublicProfileFields سرور) به ترتیب نمایش. */
export const PUBLIC_PROFILE_FIELDS = [
  'introduction',
  'address',
  'phone',
  'website',
  'working_hours',
  'instagram',
  'map_url',
] as const;

const MULTILINE = new Set<string>(['introduction', 'address', 'working_hours']);
const LTR = new Set<string>(['phone', 'website', 'instagram', 'map_url']);

type Fields = Record<string, PublicProfileField>;

function normalize(view: PublicProfileView | undefined): Fields {
  return Object.fromEntries(
    PUBLIC_PROFILE_FIELDS.map((key) => [key, view?.fields[key] ?? { value: '', isPublic: true }]),
  );
}

export function PublicProfilePage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.settings.manage');
  const [fields, setFields] = useState<Fields>(() => normalize(undefined));
  const [dirty, setDirty] = useState(false);

  const profile = useQuery({
    queryKey: pltKeys.publicProfile(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/public-profile')),
  });
  useEffect(() => {
    if (profile.data) {
      setFields(normalize(profile.data));
      setDirty(false);
    }
  }, [profile.data]);

  const onUpdated = (view: PublicProfileView) =>
    queryClient.setQueryData(pltKeys.publicProfile(session?.tenantId), view);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/platform/public-profile', {
          body: { fields, rowVersion: profile.data?.rowVersion ?? null },
        }),
      ),
    onSuccess: onUpdated,
  });
  const publish = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/public-profile:publish')),
    onSuccess: onUpdated,
  });

  const update = (key: string, patch: Partial<PublicProfileField>) => {
    setFields((current) => ({ ...current, [key]: { ...current[key]!, ...patch } }));
    setDirty(true);
  };

  const data = profile.data;
  const published = Object.entries(data?.published ?? {});
  return (
    <FormPage
      screenId={SCR_STAFF_907}
      titleKey="plt:public_profile.title"
      subtitleKey="plt:public_profile.subtitle"
      badges={
        data && (
          <Chip
            size="small"
            variant="outlined"
            icon={<PublicOutlinedIcon />}
            data-testid="public-profile-version"
            label={
              toCount(data.version) > 0
                ? t('plt:public_profile.version', { version: faDigits(toCount(data.version)) })
                : t('plt:public_profile.never_published')
            }
          />
        )
      }
      onSubmit={() => save.mutate()}
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
      isLoading={profile.isPending}
      loadError={profile.error}
      readOnly={!write.canWrite}
      submitKey="plt:public_profile.save_draft"
      notice={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {data?.hasUnpublishedChanges && !dirty && (
            <Alert
              severity="info"
              data-testid="unpublished-changes"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => publish.mutate()}
                  disabled={!write.canWrite || publish.isPending}
                  data-testid="public-profile-publish"
                >
                  {t('plt:public_profile.publish')}
                </Button>
              }
            >
              {t('plt:public_profile.unpublished')}
            </Alert>
          )}
          {publish.error ? <ErrorState error={publish.error} compact /> : null}
        </Stack>
      }
    >
      {PUBLIC_PROFILE_FIELDS.map((key) => {
        const field = fields[key]!;
        return (
          <Stack
            key={key}
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'flex-start' }}
          >
            <TextField
              label={t(`plt:public_profile.fields.${key}`)}
              value={field.value}
              onChange={(event) => update(key, { value: event.target.value })}
              fullWidth
              multiline={MULTILINE.has(key)}
              minRows={MULTILINE.has(key) ? 2 : undefined}
              disabled={!write.canWrite}
              slotProps={{
                htmlInput: {
                  dir: LTR.has(key) ? 'ltr' : undefined,
                  maxLength: key === 'introduction' ? 2000 : 500,
                  'data-testid': `public-field-${key}`,
                },
              }}
            />
            <FormControlLabel
              sx={{ minWidth: 140 }}
              control={
                <Switch
                  checked={field.isPublic}
                  onChange={(event) => update(key, { isPublic: event.target.checked })}
                  disabled={!write.canWrite}
                  slotProps={{
                    input: {
                      'aria-label': t('plt:public_profile.public_of', {
                        field: t(`plt:public_profile.fields.${key}`),
                      }),
                    },
                  }}
                  data-testid={`public-switch-${key}`}
                />
              }
              label={t('plt:public_profile.public')}
            />
          </Stack>
        );
      })}
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="published-preview">
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('plt:public_profile.published_title')}
          {data?.publishedAt && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' · '}
              <JalaliDate value={data.publishedAt} withTime />
            </Typography>
          )}
        </Typography>
        {published.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('plt:public_profile.published_empty')}
          </Typography>
        ) : (
          <KeyValueList
            items={published.map(([key, value]) => ({
              labelKey: `plt:public_profile.fields.${key}`,
              value,
              testId: `published-${key}`,
            }))}
          />
        )}
      </Paper>
    </FormPage>
  );
}
